import { subtypeChartLabels } from '@/constants';
import { dayjs } from '../../utils/dayjs';

/**
 * Utility function for extracting the children for listings data
 * This data is required by Listings Cascader in the Header
 *
 * @param {*} data Listing Data
 * @param {*} platform relevant platform
 * @returns
 */
function getListingChildren(data, platform) {
  const filteredData = data.filter((listing) => listing.platform === platform);
  const children = filteredData.map((listing) => ({
    value: `${listing.platform}-${listing.id}`,
    label: `Listing ID: ${listing.id}`,
  }));
  return children;
}

/**
 * Utility function for extracting data how the Listing Cascader requires it
 *
 * @param {*} listings All of the listings for a property
 * @returns formatted data based on platform and listing id
 */
function formatListingsForPDFSelection(listings) {
  const { onlinePlatforms } = subtypeChartLabels;
  const platforms = listings.map((listing) => listing.platform);
  // ['airbnb', 'vrbo']
  const uniquePlatforms = [...new Set(platforms)];
  const formattedData = uniquePlatforms.map((platform) => {
    const children = getListingChildren(listings, platform);
    return {
      value: platform,
      label: onlinePlatforms[platform],
      children,
    };
  });
  return formattedData;
}

function numericalMonthToString(month) {
  const dateEnum = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return dateEnum[month - 1];
}

/**
 * Gets a date from the format YYYY-MM-DD and returns year, month day in object
 * @param {*} date string YYYY-MM-DD
 */
function getDateBreakdown(date) {
  const [year, month, day] = date.split('-');
  return { year, month, day };
}

/**
 * Formats children data required by the date cascader
 *
 * @param {*} month Month is the key
 * @param {*} dates Month will be used to extract all dates with the month
 * @returns
 */
function getChildrenFromMonth(month, dates) {
  const YEAR_IDX = 2;
  // Filter only dates where the month is relevant
  const filteredDates = dates.filter(
    (date) => getDateBreakdown(date).month == month
  );
  // create children out of the relevant dates
  const children = filteredDates.map((date) => {
    const dateDetails = getDateBreakdown(date);
    return {
      value: `${month}-${dateDetails.day}`,
      label: [`${dateDetails.day}`, ' / ', `${dateDetails.year}`].join(' '),
    };
  });
  return children.sort(
    (date1, date2) => date2.label[YEAR_IDX] - date1.label[YEAR_IDX]
  );
}

const SCRAPE_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Utility function for formatting Data how the Date Cascader requires
 *
 * @param {*} listing current listing info
 * @returns data formatted correctly
 */
function getNestedOptionsForDates(dates) {
  const datesByYear = {};
  dates.forEach((dateString) => {
    const date = dayjs(dateString, SCRAPE_DATE_FORMAT);
    const datesByMonth = datesByYear[date.year()] || {};
    const datesInSameMonth = datesByMonth[date.month()] || [];

    datesByYear[date.year()] = {
      ...datesByMonth,
      [date.month()]: [...datesInSameMonth, { dayJsDate: date, dateString }],
    };
  });

  return Object.keys(datesByYear)
    .sort((a, b) => b - a)
    .map((year) => ({
      value: year,
      label: year,
      children: Object.keys(datesByYear[year])
        .sort((a, b) => b - a)
        .map((month) => ({
          value: month,
          label: dayjs.months()[month],
          children: datesByYear[year][month]
            .sort((a, b) => b.dayJsDate.diff(a.dayJsDate))
            .map(({ dateString, dayJsDate }) => ({
              value: dateString,
              label: dayJsDate.format('DD'),
            })),
        })),
    }));
}
function formatDatesForPDFSelection(listing) {
  const { pdfs = [] } = listing;

  const dates = pdfs.map((date) => date?.scrapeDate);

  return getNestedOptionsForDates(dates);
}

/**
 * Utility function for extracting the current Listing Info
 *
 * @param {*} listings All online platform listings
 * @param {*} platform Current platform
 * @param {*} listingId Current Id
 * @returns one listing which is the current listing.
 */
function getListingInfo(listings, platform, listingId) {
  if (platform === 'all')
    return listings.find((listing) => listing.id === listingId);
  const listingsData = listings.filter(
    (listing) => listing.platform === platform
  );
  const listingById = listingsData.find((listing) => listing.id === listingId);
  return listingById;
}

/**
 * PDF information for a property is NOT returned in order from the server. We need to sort them
 * to get the latest to display first.
 *
 * @param {*} listing
 * @returns latest pdf url based on scrape date
 */
function extractLatestPdfUrl(listing) {
  const sortedPdfs = listing.pdfs.sort(
    (pdf1, pdf2) => new Date(pdf2.scrapeDate) - new Date(pdf1.scrapeDate)
  );
  const firstPdfUrl = sortedPdfs[0].url;
  return firstPdfUrl;
}

/**
 * Utility function for extraction the date in the format that we use for value in Header
 * Date Cascader ie 1-14 = February 14 2020
 *
 * @param {*} date Date as is formatted in the Cascader
 * @returns Javascript Date Object
 */
function stringToDate(date) {
  const month = date.parent.label;
  const dayYear = date.label.join('');
  return new Date(`${month} / ${dayYear}`);
}

export {
  getListingInfo,
  formatDatesForPDFSelection,
  formatListingsForPDFSelection,
  stringToDate,
  getDateBreakdown,
  extractLatestPdfUrl,
};
